import * as Comlink from 'comlink';

import {
  createBrowserBackend,
  createBrowserMemoryBackend,
  OpfsUnavailableError,
} from './browser-backend.js';
import { Replica } from './replica.js';
import { type ReplicaLike } from './sync-client.js';

/**
 * Web Worker entry point. The browser path (browser.ts) creates one of
 * these per-tab; the SQLite-WASM sync API can only run inside a dedicated
 * Worker, so the Replica lives here and is exposed to the main thread
 * over Comlink (async externally / sync internally per 11-tech-stack.md §5).
 *
 * Multi-tab coordination (leader election via BroadcastChannel + Web
 * Locks) is documented in browser.ts; this worker is the leader's
 * SQLite-WASM host.
 *
 * Storage persistence fallback: when `createBrowserBackend()` throws
 * `OpfsUnavailableError` (older browser, private-mode WebKit, OPFS
 * disabled), we transparently fall back to a `:memory:` sqlite-wasm
 * backend so the GM can still author the current session. The
 * `persistent` flag is surfaced via `getPersistenceMode()` so the UI
 * can render a top-of-page warning banner. Other errors (sqlite-wasm
 * load failures, auth, network) still bubble up — only OPFS-unavailable
 * is recoverable.
 */
export type PersistenceMode = 'opfs' | 'memory';

/**
 * Cross-bundle-safe OPFS-unavailable detection. `instanceof` alone is
 * brittle because Vite can produce a separate copy of the
 * `OpfsUnavailableError` class identity inside the worker chunk vs. any
 * other consumer (and a stale worker bundle further amplifies that risk).
 * The fallback path that matters — recover when OPFS is the problem —
 * must also recognise an error by its `name` field. Anything else still
 * bubbles up so we don't swallow genuine sqlite-wasm load failures.
 */
function isOpfsUnavailable(err: unknown): boolean {
  if (err instanceof OpfsUnavailableError) return true;
  if (err && typeof err === 'object') {
    const name = (err as { name?: unknown }).name;
    if (name === 'OpfsUnavailableError') return true;
  }
  return false;
}

/**
 * Result of `open()` — the selected backend plus the OPFS rejection
 * reason when the worker fell back to `:memory:`. `opfsError` lets the
 * banner explain *why* we couldn't open OPFS instead of the generic
 * "your browser's OPFS storage didn't open" copy, which has bitten us
 * twice now in dev (Chrome 148 + concurrent stale-worker handle).
 */
export interface OpenResult {
  readonly mode: PersistenceMode;
  readonly opfsError?: string;
}

export class ReplicaWorker {
  private replica: Replica | undefined;
  private mode: PersistenceMode | null = null;

  async open(
    filename = '/dungeonlogs.db',
    forceMemory = false,
  ): Promise<OpenResult> {
    if (forceMemory) {
      const backend = await createBrowserMemoryBackend();
      this.replica = new Replica(backend);
      this.mode = 'memory';
      return { mode: 'memory' };
    }
    let opfsError: unknown;
    try {
      const backend = await createBrowserBackend(filename);
      this.replica = new Replica(backend);
      this.mode = 'opfs';
      return { mode: 'opfs' };
    } catch (err) {
      if (!isOpfsUnavailable(err)) throw err;
      opfsError = err;
    }
    // OPFS-only failure: fall back to in-memory so a single session
    // still works. The UI surfaces the lost-on-reload warning via
    // `getPersistenceMode()`. Wrap the memory open in its own try so a
    // double-broken environment (no OPFS AND no `:memory:`) surfaces a
    // specific error rather than masquerading as a generic OPFS problem.
    try {
      const backend = await createBrowserMemoryBackend();
      this.replica = new Replica(backend);
      this.mode = 'memory';
      // `OpfsUnavailableError.cause` carries the original throw from
      // the OpfsDb constructor — without it the message is a generic
      // "OPFS unavailable" sentence and we never learn the actual
      // reason.
      const cause =
        opfsError instanceof OpfsUnavailableError && opfsError.cause !== undefined
          ? opfsError.cause
          : opfsError instanceof Error
            ? opfsError.message
            : String(opfsError);
      return { mode: 'memory', opfsError: cause };
    } catch (memErr) {
      const opfsMsg = opfsError instanceof Error ? opfsError.message : String(opfsError);
      const memMsg = memErr instanceof Error ? memErr.message : String(memErr);
      const combined = new Error(
        `OPFS unavailable AND memory fallback failed: ${memMsg} (OPFS error: ${opfsMsg})`,
      );
      combined.name = 'SubstrateOpenError';
      throw combined;
    }
  }

  async getPersistenceMode(): Promise<PersistenceMode | null> {
    return this.mode;
  }

  private require(): Replica {
    if (!this.replica) throw new Error('ReplicaWorker.open() must be called first');
    return this.replica;
  }

  async appendLocal(...args: Parameters<Replica['appendLocal']>): ReturnType<Replica['appendLocal']> {
    return this.require().appendLocal(...args);
  }

  async applyServerOp(...args: Parameters<Replica['applyServerOp']>): ReturnType<Replica['applyServerOp']> {
    return this.require().applyServerOp(...args);
  }

  // Batched backfill apply (#sync perf) — MUST be forwarded here for the
  // same Comlink reason as the others; this is the whole point of the
  // batch path (one worker round-trip for the whole backfill).
  async applyServerOps(
    ...args: Parameters<Replica['applyServerOps']>
  ): ReturnType<Replica['applyServerOps']> {
    return this.require().applyServerOps(...args);
  }

  async getHead(...args: Parameters<Replica['getHead']>): ReturnType<Replica['getHead']> {
    return this.require().getHead(...args);
  }

  async iterateOpLog(...args: Parameters<Replica['iterateOpLog']>): ReturnType<Replica['iterateOpLog']> {
    return this.require().iterateOpLog(...args);
  }

  async getChainOps(
    ...args: Parameters<Replica['getChainOps']>
  ): ReturnType<Replica['getChainOps']> {
    return this.require().getChainOps(...args);
  }

  /**
   * III.A.2 (#62) — per-Viewer sync watermark, forwarded to the worker-
   * resident Replica. SyncClient.connect() probes `replica.getViewerWatermark`
   * before calling it; on a Comlink proxy that probe is always truthy
   * (Comlink wraps the proxy so any property access returns a callable),
   * so when the method is missing on the underlying class the call
   * resolves to `undefined.apply(...)` inside the worker and surfaces as
   * a `TypeError: Cannot read properties of undefined (reading 'apply')`.
   * That throw propagates back through `client.connect()` → the catch
   * arm in app.ts → the `failed` substrate-banner, where the user sees
   * "Storage unavailable — saves will not work." Always forward these
   * here so the Comlink probe finds a real method.
   */
  async getViewerWatermark(
    ...args: Parameters<Replica['getViewerWatermark']>
  ): ReturnType<Replica['getViewerWatermark']> {
    return this.require().getViewerWatermark(...args);
  }

  async setViewerWatermark(
    ...args: Parameters<Replica['setViewerWatermark']>
  ): ReturnType<Replica['setViewerWatermark']> {
    return this.require().setViewerWatermark(...args);
  }

  /**
   * III.A.2 (#80) — local op-log max HLC, forwarded to the worker-
   * resident Replica. MUST be forwarded here for the same Comlink-probe
   * reason documented on `getViewerWatermark` above: `SyncClient.connect`
   * probes `replica.maxOpHlc` (always truthy over a Comlink proxy) and
   * calls it; a missing facade method resolves to `undefined.apply(...)`
   * in the worker and surfaces as the "Storage unavailable" banner.
   */
  async maxOpHlc(...args: Parameters<Replica['maxOpHlc']>): ReturnType<Replica['maxOpHlc']> {
    return this.require().maxOpHlc(...args);
  }

  /**
   * Durable outbox (audit fix) — MUST be forwarded for the same Comlink-probe
   * reason documented on `getViewerWatermark` above. `SyncClient.connect()`
   * calls `reenqueueUnconfirmed()`, which reaches this method on every
   * connect; when it was missing the resulting `undefined.apply(...)` threw
   * out of `connect()` and painted the "Storage unavailable" banner on EVERY
   * boot — the substrate looked dead even though OPFS had opened fine.
   */
  async listUnconfirmedOps(
    ...args: Parameters<Replica['listUnconfirmedOps']>
  ): ReturnType<Replica['listUnconfirmedOps']> {
    return this.require().listUnconfirmedOps(...args);
  }

  /**
   * Rejected-op removal — reached from the `op_reject_stale` /
   * `op_reject_invariant` arms so a server-rejected op stops being folded as
   * canon. Same forwarding requirement as above.
   */
  async removeLocalOp(
    ...args: Parameters<Replica['removeLocalOp']>
  ): ReturnType<Replica['removeLocalOp']> {
    return this.require().removeLocalOp(...args);
  }

  /**
   * Out-of-band confirmation of a locally-authored op. Not on the
   * `SyncClient` hot path today (the accept round-trip stamps
   * `confirmed_hlc` inside `applyServerOp`), but it is public on `Replica`
   * and structured-cloneable, so it is forwarded rather than left as a
   * latent `undefined.apply(...)` for the first caller that needs it.
   */
  async markOpConfirmed(
    ...args: Parameters<Replica['markOpConfirmed']>
  ): ReturnType<Replica['markOpConfirmed']> {
    return this.require().markOpConfirmed(...args);
  }

  async close(): Promise<void> {
    await this.replica?.close();
    this.replica = undefined;
    this.mode = null;
  }
}

/* -------------------------------------------------------------------------- *
 * Facade-completeness gates (compile-time)
 *
 * Twice now a method was added to `Replica` + called from the main thread but
 * not forwarded here, and each time it surfaced as the same baffling
 * `TypeError: Cannot read properties of undefined (reading 'apply')` →
 * "Storage unavailable" banner, because a Comlink proxy makes every property
 * access truthy and callable (so no runtime probe can catch it). These two
 * type-level assertions turn that class of bug into a typecheck failure.
 * -------------------------------------------------------------------------- */

/**
 * Gate 1 — every method `SyncClient` may call is forwarded.
 *
 * `ReplicaLike` is the contract `SyncClient` consumes, and it is deliberately
 * all-required: making a member optional would let this assignment succeed
 * with the method absent, which is precisely the hole that shipped the bug.
 */
const _facadeSatisfiesReplicaLike = (
  remote: Comlink.Remote<ReplicaWorker>,
): ReplicaLike => remote;
void _facadeSatisfiesReplicaLike;

/**
 * Gate 2 — every PUBLIC member of `Replica` is either forwarded or explicitly
 * declared worker-internal. `keyof` skips TypeScript `private` members, so
 * this tracks the real public surface and updates itself: add a public method
 * to `Replica` and this stops compiling until you forward it here or add it to
 * the exclusion list below with a reason.
 *
 * `initialReplicate` is the one deliberate exclusion: its first parameter is
 * an `AsyncIterable`, which is not structured-cloneable and therefore cannot
 * cross a Comlink boundary at all. The main thread's batch-bootstrap path is
 * `applyServerOps` (forwarded above), which takes a plain array.
 */
type WorkerInternalReplicaMembers = 'initialReplicate';

type UnforwardedReplicaMembers = Exclude<
  Exclude<keyof Replica, WorkerInternalReplicaMembers>,
  keyof ReplicaWorker
>;

const _everyReplicaMemberIsForwarded: UnforwardedReplicaMembers extends never
  ? true
  : ['UNFORWARDED Replica members — forward them on ReplicaWorker', UnforwardedReplicaMembers] =
  true;
void _everyReplicaMemberIsForwarded;

/**
 * Self-expose ONLY when this module is actually running as a dedicated
 * worker. `Comlink.expose` wires `addEventListener`/`postMessage` on the
 * global endpoint; in any other context (a vitest module import, SSR, a
 * bundler's module-graph analysis) that global is not a message endpoint and
 * `expose` throws `ep.addEventListener is not a function` at import time.
 *
 * Guarding it is what makes the facade unit-testable — `worker-facade.test.ts`
 * imports `ReplicaWorker` to assert the Comlink surface is complete, and that
 * gate is the only thing standing between a missing forward and the
 * "Storage unavailable" banner (see the class doc above). The real worker path
 * is unchanged: inside `worker-entry.js` spawned via `new Worker(...)` the
 * predicate holds and `expose` runs exactly as before.
 *
 * Duck-typed rather than `self instanceof DedicatedWorkerGlobalScope`: this
 * module is compiled under several tsconfigs (the client app's does not
 * include the `WebWorker` lib), so the interface name is not universally in
 * scope. The shape test is equivalent — a dedicated worker global has the
 * message-endpoint methods AND no `window`, which is exactly what separates it
 * from both the main thread (`self === window`) and Node (`self` undefined).
 */
function isDedicatedWorkerScope(): boolean {
  if (typeof self === 'undefined') return false;
  if (typeof (globalThis as { window?: unknown }).window !== 'undefined') return false;
  const scope = self as unknown as {
    postMessage?: unknown;
    addEventListener?: unknown;
  };
  return typeof scope.postMessage === 'function' && typeof scope.addEventListener === 'function';
}

if (isDedicatedWorkerScope()) {
  Comlink.expose(new ReplicaWorker());
}
